import { joinURL } from '../../../../utilities';
import {
  communicationTabPathName,
  medicalCollaboratorLetterGuide,
  serviceProviderAreaPathName,
} from '../citizen-serviceprovider-area-pathnames';

export enum ServiceProviderRoute {
  DETAILS = 'DETAILS',
  CONTACT_INFO = 'CONTACT_INFO',
  COMMUNICATION = 'COMMUNICATION',
  DIARYANDDOCUMENTS = 'DIARYANDDOCUMENTS',
  DIARY_DOCUMENT_WIZARD = 'DIARY_DOCUMENT_WIZARD',
  MEDICAL_COLLABORATION = 'MEDICAL_COLLABORATION',
  MEDICINE = 'MEDICINE',
  CALENDAR = 'CALENDAR',
  TRAFFIC_LIGHT = 'TRAFFIC_LIGHT',
  HEALTH_OVERVIEW = 'HEALTH_OVERVIEW',
  USE_OF_FORCE = 'USE_OF_FORCE',
  USE_OF_FORCE_INCIDENT = 'USE_OF_FORCE_INCIDENT',
  USE_OF_FORCE_AUTHORIZATION_REQUEST = 'USE_OF_FORCE_AUTHORIZATION_REQUEST',
  USE_OF_FORCE_SUMMARY_REGISTRATION = 'USE_OF_FORCE_SUMMARY_REGISTRATION',
  PARTIAL_OBJECTIVES_OBSERVATIONS = 'PARTIAL_OBJECTIVES_OBSERVATIONS',
  MEDICAL_REQUEST_CREATE = 'MEDICAL_REQUEST_CREATE',
  MEDICAL_REQUEST_CREATE_271 = 'MEDICAL_REQUEST_CREATE_271',
  CORRESPONDENCE_DETAIL = 'CORRESPONDENCE_DETAIL',
  MEDICAL_COLLABORATOR_LETTER_GUIDE = 'MEDICAL_COLLABORATOR_LETTER_GUIDE',
}

interface RouteConfig {
  path: string;
  requiresSpuId?: boolean;
}

export const serviceProviderRouteConfig: Record<
  ServiceProviderRoute,
  RouteConfig
> = {
  [ServiceProviderRoute.DETAILS]: {
    path: 'details',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.CONTACT_INFO]: {
    path: 'contactinformation',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.COMMUNICATION]: {
    path: 'communication',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.DIARYANDDOCUMENTS]: {
    path: 'diaryanddocuments',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.DIARY_DOCUMENT_WIZARD]: {
    path: 'diary/document/create',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.MEDICAL_COLLABORATION]: {
    path: 'medical-collaboration',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.MEDICINE]: {
    path: 'medicine',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.CALENDAR]: {
    path: 'calendar',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.TRAFFIC_LIGHT]: {
    path: 'traffic-light',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.HEALTH_OVERVIEW]: {
    path: 'health-overview',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.USE_OF_FORCE]: {
    path: 'use-of-force',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.USE_OF_FORCE_INCIDENT]: {
    path: 'use-of-force/incident',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.USE_OF_FORCE_AUTHORIZATION_REQUEST]: {
    path: 'use-of-force/authorization-request',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.USE_OF_FORCE_SUMMARY_REGISTRATION]: {
    path: 'use-of-force/summary-registration',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.PARTIAL_OBJECTIVES_OBSERVATIONS]: {
    path: 'partial-objectives-observations',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.MEDICAL_REQUEST_CREATE]: {
    path: 'medical-collaboration/medical-request',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.MEDICAL_REQUEST_CREATE_271]: {
    path: 'medical-collaboration/create-request-271',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.CORRESPONDENCE_DETAIL]: {
    path: 'medical-collaboration/correspondence',
    requiresSpuId: true,
  },
  [ServiceProviderRoute.MEDICAL_COLLABORATOR_LETTER_GUIDE]: {
    path: `${communicationTabPathName}/${medicalCollaboratorLetterGuide}`,
    requiresSpuId: true,
  },
};

export interface ServiceProviderUrlBuilderParams {
  citizenId: string;
  spuId?: string;
  documentId?: string;
  medicalRequestId?: string;
  correspondenceId?: string;
  incidentId?: string;
  authorizationRequestId?: string;
  summaryRegistrationId?: string;
  queryParams?: Record<string, string>;
}

export class ServiceProviderUrlBuilder {
  /**
   * Builds a URL for a service provider route
   * @param route The service provider route enum value
   * @param params URL builder parameters including citizenId, spuId, and optional query params
   * @returns The complete URL with query string
   */
  static build(
    route: ServiceProviderRoute,
    params: ServiceProviderUrlBuilderParams
  ): string {
    const config = serviceProviderRouteConfig[route];
    const {
      citizenId,
      spuId,
      documentId,
      medicalRequestId,
      correspondenceId,
      incidentId,
      authorizationRequestId,
      summaryRegistrationId,
      queryParams = {},
    } = params;

    let basePath = joinURL(
      '/',
      'citizen',
      serviceProviderAreaPathName,
      citizenId,
      config.path
    );

    // Append documentId to the path for DIARY_DOCUMENT_WIZARD route when editing
    if (route === ServiceProviderRoute.DIARY_DOCUMENT_WIZARD && documentId) {
      basePath = joinURL(basePath, documentId);
    }

    // Append medicalRequestId to the path for MEDICAL_REQUEST_CREATE route when viewing an existing request
    if (
      route === ServiceProviderRoute.MEDICAL_REQUEST_CREATE &&
      medicalRequestId
    ) {
      basePath = joinURL(basePath, medicalRequestId);
    }

    // Append correspondenceId to the path for CORRESPONDENCE_DETAIL route when viewing a message
    if (
      route === ServiceProviderRoute.CORRESPONDENCE_DETAIL &&
      correspondenceId
    ) {
      basePath = joinURL(basePath, correspondenceId);
    }

    if (route === ServiceProviderRoute.USE_OF_FORCE_INCIDENT && incidentId) {
      basePath = joinURL(basePath, incidentId);
    }

    if (
      route === ServiceProviderRoute.USE_OF_FORCE_AUTHORIZATION_REQUEST &&
      authorizationRequestId
    ) {
      basePath = joinURL(basePath, authorizationRequestId);
    }

    if (
      route === ServiceProviderRoute.USE_OF_FORCE_SUMMARY_REGISTRATION &&
      summaryRegistrationId
    ) {
      basePath = joinURL(basePath, summaryRegistrationId);
    }

    const allQueryParams = { ...queryParams };
    if (config.requiresSpuId && spuId) {
      allQueryParams.spuid = spuId;
    }

    const queryString = new URLSearchParams(allQueryParams).toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }

  /**
   * Builds a URL for a custom tab path (for backwards compatibility or dynamic tabs)
   * @param tabPath The tab path segment
   * @param params URL builder parameters
   * @returns The complete URL with query string
   */
  static buildCustomUrl(
    tabPath: string,
    params: ServiceProviderUrlBuilderParams
  ): string {
    const { citizenId, spuId, queryParams = {} } = params;

    const basePath = joinURL(
      '/',
      'citizen',
      serviceProviderAreaPathName,
      citizenId,
      tabPath
    );

    const allQueryParams = { ...queryParams };
    if (spuId) {
      allQueryParams.spuid = spuId;
    }

    const queryString = new URLSearchParams(allQueryParams).toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
  }
}
